import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import talentLogo from "../assets/talentlogo.png";
import {
  formatAppDateTime,
  formatAppIntlLabel,
  getAppDisplayTimezone,
  parseDbDateTime,
} from "../utils/appDateTime";

function resolveSubmittedActionTime(checkinData, submittedActionTimestamp, isCheckout) {
  const submittedTimestamp = parseDbDateTime(submittedActionTimestamp);
  if (submittedTimestamp) return submittedTimestamp;

  const payload = checkinData?.data || checkinData || {};
  const candidates = isCheckout
    ? [
        payload.checkoutDate,
        payload.checkOutDate,
        payload.checkout_date,
        payload.checkoutTime,
        payload.checkout_time,
        payload.checkoutAt,
        payload.check_out_at,
        payload.checkedOutAt,
        payload.checked_out_at,
        payload.actualEndDate,
        payload.actual_end_date,
        payload.actionTimestamp,
        payload.action_timestamp,
      ]
    : [
        payload.checkinDate,
        payload.checkInDate,
        payload.checkin_date,
        payload.checkinTime,
        payload.checkin_time,
        payload.checkinAt,
        payload.check_in_at,
        payload.checkedInAt,
        payload.checked_in_at,
        payload.actualStartDate,
        payload.actual_start_date,
        payload.actionTimestamp,
        payload.action_timestamp,
      ];

  for (const candidate of candidates) {
    const parsed = parseDbDateTime(candidate);
    if (parsed) return parsed;
  }

  return null;
}

function resolveTimestampFromSources(sources, fieldNames) {
  for (const source of sources) {
    if (!source) continue;
    const payload = source?.data || source;
    for (const fieldName of fieldNames) {
      const parsed = parseDbDateTime(payload?.[fieldName]);
      if (parsed) return parsed;
    }
  }
  return null;
}

function formatDurationLabel(startInstant, endInstant) {
  const start = parseDbDateTime(startInstant);
  const end = parseDbDateTime(endInstant);
  if (!start || !end) return "";

  const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

function resolveSuccessShiftDetails({ qrData, user, successShiftDetails, isCheckout }) {
  if (successShiftDetails) return successShiftDetails;

  if (isCheckout) {
    return (
      user?.checkedInShiftDetails ||
      user?.checkedInShift ||
      user?.currentShiftDetails ||
      user?.currentShift ||
      user?.activeShiftDetails ||
      user?.activeShift ||
      user?.scheduledShiftDetails ||
      qrData?.shiftDetails ||
      null
    );
  }

  return user?.scheduledShiftDetails || qrData?.shiftDetails || null;
}

function getShiftStart(shiftDetails) {
  return shiftDetails?.startDate || shiftDetails?.start_date || shiftDetails?.startDateIso || null;
}

function getShiftEnd(shiftDetails) {
  return shiftDetails?.endDate || shiftDetails?.end_date || shiftDetails?.endDateIso || null;
}

export default function QRSuccess() {
  usePageMeta("Check In Success");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    qrData,
    user,
    checkinData,
    isCheckout,
    submittedActionTimestamp,
    successShiftDetails,
  } = location.state || {};
  const [currentTime, setCurrentTime] = useState(new Date());
  const [countdown, setCountdown] = useState(5);
  const submittedActionTime = resolveSubmittedActionTime(
    checkinData,
    submittedActionTimestamp,
    isCheckout,
  );
  const displayedShiftDetails = resolveSuccessShiftDetails({
    qrData,
    user,
    successShiftDetails,
    isCheckout,
  });
  const displayedShiftStart = getShiftStart(displayedShiftDetails);
  const displayedShiftEnd = getShiftEnd(displayedShiftDetails);
  const checkoutShiftStart = isCheckout
    ? resolveTimestampFromSources(
        [
          checkinData,
          successShiftDetails,
          displayedShiftDetails,
          user?.checkedInShiftDetails,
          user?.checkedInShift,
          user?.currentShiftDetails,
          user?.currentShift,
          user?.activeShiftDetails,
          user?.activeShift,
        ],
        [
          "checkinDate",
          "checkInDate",
          "checkin_date",
          "checkinTime",
          "checkin_time",
          "checkinAt",
          "check_in_at",
          "checkedInAt",
          "checked_in_at",
          "actualStartDate",
          "actual_start_date",
          "start",
        ],
      ) || displayedShiftStart
    : displayedShiftStart;
  const displayedShiftEffectiveEnd = isCheckout
    ? submittedActionTime || displayedShiftEnd
    : displayedShiftEnd;

  useEffect(() => {
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const redirectTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(redirectTimer);
          navigate("/checkin" + window.location.search);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(redirectTimer);
    };
  }, [navigate]);

  const formatTime = (date) =>
    formatAppIntlLabel(date, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: getAppDisplayTimezone(),
    });

  if (!user || !qrData) {
    navigate("/checkin" + window.location.search);
    return null;
  }

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-bold text-center mb-2 text-green-600">
          {isCheckout ? "Check Out Successful!" : "Check In Successful!"}
        </h1>

        <h2 className="text-lg font-semibold text-center mb-4 text-gray-800">
          {isCheckout ? `Thank you, ${user.firstName}!` : `Welcome, ${user.firstName}!`}
        </h2>

        {/* Current Time */}
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-600">
            {formatAppIntlLabel(currentTime, {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
              timeZone: getAppDisplayTimezone(),
            })}
          </div>
        </div>

        {/* Check-in/Check-out Details */}
        <div className="bg-green-50 p-3 rounded-lg mb-4">
          <div className="text-sm text-green-800">
            <div className="font-semibold">Location: {qrData.clientDetails?.clientName || "Unknown"}</div>
            <div>Account: {user.firstName} {user.lastName}</div>
            <div>Method: QR Code + Face Recognition</div>
            <div>Face Match: {user.similarity?.toFixed(1) || "N/A"}%</div>
            {checkinData && (
              <>
                {isCheckout ? (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <div className="font-semibold">
                      Check-out Time: {formatTime(submittedActionTime || currentTime)}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <div className="font-semibold">
                      Check-in Time: {formatTime(submittedActionTime || currentTime)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Shift Details */}
        {displayedShiftDetails && checkoutShiftStart && displayedShiftEffectiveEnd && (
          <div className="bg-blue-50 p-3 rounded-lg mb-4">
            <div className="text-sm text-blue-800">
              <div className="font-semibold">Shift Time:</div>
              <div>
                {isCheckout
                  ? `${formatAppDateTime(checkoutShiftStart)} - ${
                      formatAppDateTime(displayedShiftEffectiveEnd)
                    }, ${formatDurationLabel(
                      checkoutShiftStart,
                      displayedShiftEffectiveEnd,
                    )} total`
                  : `${formatAppDateTime(displayedShiftStart)} - ${formatAppDateTime(
                      displayedShiftEnd,
                    )}`}
              </div>
              {displayedShiftDetails.position && (
                <div>Position: {displayedShiftDetails.position}</div>
              )}
            </div>
          </div>
        )}

        {/* Auto-redirect notice */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">
            Redirecting to check-in page in {countdown} seconds...
          </p>
          <p className="text-xs text-gray-500 mt-1">Next person can use the system</p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => navigate("/checkin" + window.location.search)}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] transition-colors"
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}
